/**
 * Evaluation / temperament day (migration 0099) — shared types + fetchers
 * for the staff app. Mirrors the API shapes in
 * `apps/api/src/routes/assessments.ts`.
 */

export type AssessmentStatus =
  | 'not_required'
  | 'required'
  | 'scheduled'
  | 'passed'
  | 'failed'
  | 'expired';

export const ASSESSMENT_STATUS_LABEL: Record<AssessmentStatus, string> = {
  not_required: 'No evaluation needed',
  required: 'Evaluation required',
  scheduled: 'Evaluation booked',
  passed: 'Evaluation passed',
  failed: 'Evaluation not passed',
  expired: 'Evaluation expired',
};

/** Tailwind classes per status for the chip. */
export const ASSESSMENT_STATUS_TONE: Record<AssessmentStatus, string> = {
  not_required: 'bg-secondary text-muted-foreground ring-border',
  required: 'bg-warning/10 text-warning-foreground ring-warning/30',
  scheduled: 'bg-primary/10 text-primary ring-primary/30',
  passed: 'bg-success/10 text-success ring-success/30',
  failed: 'bg-destructive/10 text-destructive ring-destructive/30',
  expired: 'bg-warning/10 text-warning-foreground ring-warning/30',
};

export interface AssessmentSettingsView {
  daycareRequiresAssessment: boolean;
  boardingRequiresAssessment: boolean;
  assessmentValidityMonths: number | null;
  assessmentServiceId: string | null;
  ownerMustStay: boolean;
  assessmentNoteHtml: string | null;
}

export interface AssessmentServiceSummary {
  id: string;
  name: string;
  category: string;
  isActive: boolean;
  defaultDurationMin: number | null;
  defaultPriceMinor: number;
}

export interface AssessmentSettingsResponse {
  settings: AssessmentSettingsView;
  service: AssessmentServiceSummary | null;
}

export interface AssessmentOutcomeRow {
  id: string;
  petId: string;
  bookingId: string | null;
  outcome: 'passed' | 'failed';
  notes: string | null;
  recordedByMembershipId: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface OpenAssessmentBooking {
  bookingId: string;
  status: string;
  scheduledAt: string | null;
  durationMin: number | null;
  serviceId: string;
}

export interface PetAssessmentView {
  petId: string;
  petName: string;
  status: AssessmentStatus;
  passedAt: string | null;
  expiresAt: string | null;
  openBooking: OpenAssessmentBooking | null;
  outcomes: AssessmentOutcomeRow[];
  bookAssessmentUrl: string;
  settings: {
    daycareRequiresAssessment: boolean;
    boardingRequiresAssessment: boolean;
    assessmentValidityMonths: number | null;
    assessmentServiceId: string | null;
  };
}

export interface BookingAssessmentView {
  isAssessment: boolean;
  bookingId?: string;
  bookingStatus?: string;
  scheduledAt?: string | null;
  durationMin?: number | null;
  serviceName?: string;
  ownerMustStay?: boolean;
  validityMonths?: number | null;
  pet?: {
    id: string;
    name: string;
    assessmentStatus: AssessmentStatus;
    assessmentPassedAt: string | null;
    assessmentExpiresAt: string | null;
  };
  outcomes?: AssessmentOutcomeRow[];
  traits?: readonly string[];
}

export interface RecordOutcomeBody {
  outcome: 'passed' | 'failed';
  notes: string | null;
  observations: { traits: string[]; notes: string | null } | null;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; issues?: string[] };
    throw new Error(body.issues?.[0] ?? body.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

const JSON_HEADERS = { 'content-type': 'application/json' };

export async function getAssessmentSettings(): Promise<AssessmentSettingsResponse> {
  return json(await fetch('/api/assessments/settings', { credentials: 'include' }));
}

export async function updateAssessmentSettings(
  patch: Partial<AssessmentSettingsView>,
): Promise<AssessmentSettingsResponse> {
  return json(
    await fetch('/api/assessments/settings', {
      method: 'PUT',
      credentials: 'include',
      headers: JSON_HEADERS,
      body: JSON.stringify(patch),
    }),
  );
}

export async function createDefaultAssessmentService(input: {
  name?: string;
  durationMin?: number;
  priceMinor?: number;
}): Promise<AssessmentSettingsResponse> {
  return json(
    await fetch('/api/assessments/settings/service', {
      method: 'POST',
      credentials: 'include',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    }),
  );
}

export async function getBookingAssessment(bookingId: string): Promise<BookingAssessmentView> {
  return json(await fetch(`/api/bookings/${bookingId}/assessment`, { credentials: 'include' }));
}

export async function recordBookingAssessmentOutcome(
  bookingId: string,
  body: RecordOutcomeBody,
): Promise<{ pet: { assessmentStatus: AssessmentStatus } }> {
  return json(
    await fetch(`/api/bookings/${bookingId}/assessment-outcome`, {
      method: 'POST',
      credentials: 'include',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    }),
  );
}

export async function getPetAssessment(petId: string): Promise<PetAssessmentView> {
  return json(await fetch(`/api/pets/${petId}/assessment`, { credentials: 'include' }));
}

export async function recordPetAssessmentOutcome(
  petId: string,
  body: RecordOutcomeBody,
): Promise<{ pet: { assessmentStatus: AssessmentStatus } }> {
  return json(
    await fetch(`/api/pets/${petId}/assessment-outcome`, {
      method: 'POST',
      credentials: 'include',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    }),
  );
}

/** Human label for a raw trait slug ('dog_social' → 'Dog social'). */
export function traitLabel(trait: string): string {
  const s = trait.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
